import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") navigate("/admin");
    else if (user.status === "approved") navigate("/portal");
    else if (user.status === "pending_onboarding") navigate("/onboarding");
    else navigate("/pending");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u.role === "admin") navigate("/admin");
      else if (u.status === "approved") navigate("/portal");
      else if (u.status === "pending_onboarding") navigate("/onboarding");
      else navigate("/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-[#2A2A2A] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Film crew on a soundstage" className="w-full h-full object-cover grayscale contrast-125 opacity-60" />
        <div className="absolute inset-0 flex flex-col justify-end p-10">
          <div>
            <h2 className="font-bebas text-6xl leading-[0.9]">CREATIVE WORK,<br /><span className="text-[#FF4D00]">MANAGED RIGHT.</span></h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white mb-8 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
          <img src={LOGO_URL} alt="Splash Creative Hub" className="w-[225px] max-w-full h-auto object-contain mb-8 mx-auto block" />
          <div className="text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-3">Account Access</div>
          <h1 className="font-bebas text-5xl mb-8">SIGN IN</h1>

          <form onSubmit={submit} className="space-y-4">
            <input data-testid="auth-email-input" type="email" className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input data-testid="auth-password-input" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <button data-testid="auth-submit-btn" disabled={loading} className="w-full bg-[#FF4D00] text-white py-3 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>

          <div className="mt-6 text-sm text-[#A1A1AA] space-y-2">
            <div>New to the Splash Team? <Link data-testid="apply-link" to="/join" className="text-[#FF4D00] hover:underline">Apply here</Link></div>
            <div>Applied already? <Link data-testid="check-status-link" to="/status" className="text-[#FF4D00] hover:underline">Check your status</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
